import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff, LogIn, RefreshCw, AlertCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/1tzx91jj_504423129_17847547452489584_2138028376177138441_n.jpg";

const AdminLogin = () => {
  const { t } = useLanguage();
  const { login, isAuthenticated, loading, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Clear errors when inputs change
  useEffect(() => {
    if (error) setError('');
    if (clearError) clearError();
  }, [email, password]);

  // If already authenticated, redirect to dashboard
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016] mx-auto mb-4"></div>
          <p className="text-[#4A5D43] text-sm">Yüklənir...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Email və şifrə daxil edin');
      return;
    }
    
    setError('');
    setIsLoading(true);

    try {
      await login(email.trim(), password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Giriş uğursuz oldu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4" data-testid="admin-login-page">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#2D5016]/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#D97706]/5 to-transparent" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="Oriva Quba" className="h-20 w-auto rounded-xl shadow-lg" />
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 border border-[#E5E3DB]">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">{t('admin_login')}</h1>
            <p className="text-[#4A5D43] text-sm mt-2">Oriva Quba Admin Panel</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-600 text-sm">{error}</p>
                  {(error.includes('Server') || error.includes('əlaqə')) && (
                    <button
                      type="button"
                      onClick={handleRefresh}
                      className="mt-2 flex items-center gap-2 text-sm text-red-700 hover:text-red-800 font-medium"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Səhifəni yenilə
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1A2415] mb-2">
                {t('admin_email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="admin-input rounded-lg"
                placeholder="admin@oriva.az"
                required
                autoComplete="email"
                disabled={isLoading}
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2415] mb-2">
                {t('admin_password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="admin-input rounded-lg pr-12"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5D43] hover:text-[#2D5016]"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#2D5016] text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#1A2415] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Giriş edilir...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  {t('admin_login_btn')}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-[#D97706] hover:underline">
              &larr; {t('nav_home')}
            </a>
          </div>
        </div>

        {/* Help text */}
        <div className="text-center mt-4 space-y-1">
          <p className="text-xs text-[#4A5D43]">
            Giriş problemi? Səhifəni yeniləyib yenidən cəhd edin.
          </p>
          <button
            onClick={handleRefresh}
            className="text-xs text-[#D97706] hover:underline"
          >
            Səhifəni yenilə
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
